import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useClipboard,
  useToast,
  Wrap,
} from "@chakra-ui/react";

import React from "react";
import {
  IRegistryChoiceOptions,
  IRegistryDataColumn,
  IRegistryDataColumnMapping,
  RegistryDataColumnTypeEnum,
} from "../../models/RegistryDataColumn";
import { IconTextSmudge } from "../shared/IconTextSmudge";
import { IMaterialIconProps, MaterialIcon } from "../shared/MaterialIcon";

const OPTION_COLUMNS_TYPES: RegistryDataColumnTypeEnum[] = [
  RegistryDataColumnTypeEnum.checkbox,
  RegistryDataColumnTypeEnum.radio,
  RegistryDataColumnTypeEnum.dropdown,
  RegistryDataColumnTypeEnum.yesno,
];

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  column: IRegistryDataColumn;
}

interface IFieldOptionsProps {
  options?: IRegistryChoiceOptions;
}
export const FieldTypeOptions = ({ options }: IFieldOptionsProps) => {
  return (
    <Stack spacing={1}>
      <SimpleGrid columns={2} spacingX={4} spacingY="1">
        <Box>
          <Text textStyle={"label"}>Option label</Text>
        </Box>
        <Box>
          <Text textStyle={"label"}>Value in database</Text>
        </Box>
      </SimpleGrid>
      <SimpleGrid columns={2} spacingX={4} spacingY="1">
        {options &&
          Object.entries(options).map(([key, option]) => (
            <React.Fragment key={`${key}`}>
              <Box key={`${key}-label`}>
                <IconTextSmudge
                  iconColor="text.soft"
                  iconName={"label"}
                  text={option.label}
                ></IconTextSmudge>
              </Box>
              <Box key={`${key}-value`}>
                <IconTextSmudge
                  iconColor="text.soft"
                  iconName={"key"}
                  text={option.value}
                ></IconTextSmudge>
              </Box>
            </React.Fragment>
          ))}
      </SimpleGrid>
      {(!options || !Object.entries(options).length) && (
        <Text color="text.softer">Missing data</Text>
      )}
    </Stack>
  );
};
interface IFieldMappingsProps {
  mappings?: IRegistryDataColumnMapping[];
}
export const FieldMappings = ({ mappings }: IFieldMappingsProps) => {
  return (
    <Stack spacing={1}>
      <SimpleGrid columns={2} spacingX={4} spacingY="1">
        <Box>
          <Text textStyle={"label"}>Registry Mapping</Text>
        </Box>
        <Box>
          <Text textStyle={"label"}>Key in registry</Text>
        </Box>
      </SimpleGrid>
      <SimpleGrid columns={2} spacingX={4} spacingY="1">
        {mappings &&
          mappings.map((mapping) => (
            <React.Fragment key={`${mapping.registry_title}`}>
              <Box key={`${mapping.registry_title}-label`}>
                <IconTextSmudge
                  iconColor="text.soft"
                  iconName={"diversity_2"}
                  text={mapping.registry_title}
                ></IconTextSmudge>
              </Box>
              <Box key={`${mapping.registry_title}-value`}>
                <IconTextSmudge
                  iconColor="text.soft"
                  iconName={"transform"}
                  text={mapping.registry_key}
                ></IconTextSmudge>
              </Box>
            </React.Fragment>
          ))}
      </SimpleGrid>
      {!mappings && <Text color="text.softer">Missing data</Text>}
    </Stack>
  );
};

export const RegistryHeaderModal = ({ onClose, isOpen, column }: IProps) => {
  const [iconNameFieldType, setIconNameFieldType] =
    React.useState<IMaterialIconProps["name"]>("text_fields");

  const toast = useToast();

  const { onCopy: onCopyHelpText, value: valueHelpText } = useClipboard(
    `${column?.help_text}`
  );

  const { onCopy: onCopyId, value: valueId } = useClipboard(
    `${column?.column_key}`
  );

  const { onCopy: onCopyFieldId, value: valueFieldId } = useClipboard(
    `${column?.fieldversion_id}`
  );

  const handleOnCopyHelpText = () => {
    onCopyHelpText();

    toast({
      title: "Copied",
      description: valueHelpText,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleOnCopyId = () => {
    onCopyId();

    toast({
      title: "Copied",
      description: valueId,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleOnCopyFieldId = () => {
    onCopyFieldId();

    toast({
      title: "Copied",
      description: valueFieldId,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleOnClose = () => {
    onClose();
  };

  React.useEffect(() => {
    // text
    // descriptive
    // notes
    let iconNameFieldTypeUpdate: IMaterialIconProps["name"] = "text_fields";

    if (column.type === RegistryDataColumnTypeEnum.checkbox) {
      // checkbox
      iconNameFieldTypeUpdate = "checklist";
    } else if (column.type === RegistryDataColumnTypeEnum.radio) {
      // radio
      iconNameFieldTypeUpdate = "radio_button_checked";
    } else if (column.type === RegistryDataColumnTypeEnum.dropdown) {
      // dropdown
      iconNameFieldTypeUpdate = "list";
    } else if (column.type === RegistryDataColumnTypeEnum.email) {
      // email
      iconNameFieldTypeUpdate = "alternate_email";
    } else if (column.type === RegistryDataColumnTypeEnum.calc) {
      // calc
      iconNameFieldTypeUpdate = "calculate";
    } else if (column.type === RegistryDataColumnTypeEnum.calc_on_read) {
      iconNameFieldTypeUpdate = "calculate";
    } else if (column.type === RegistryDataColumnTypeEnum.calc_on_write) {
      iconNameFieldTypeUpdate = "calculate";
    } else if (column.type === RegistryDataColumnTypeEnum.number) {
      // number
      iconNameFieldTypeUpdate = "123";
    } else if (column.type === RegistryDataColumnTypeEnum.file) {
      // file
      iconNameFieldTypeUpdate = "description";
    } else if (column.type === RegistryDataColumnTypeEnum.yesno) {
      // yesno
      iconNameFieldTypeUpdate = "toggle_on";
    }

    setIconNameFieldType(iconNameFieldTypeUpdate);
  }, [column]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      closeOnOverlayClick={true}
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Column details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={8}>
            <Stack spacing={0} px={4}>
              <Text textStyle={"title"} color="text.primary" fontSize="3xl">
                {column.display}
              </Text>
              <Text color="text.soft">{column.help_text}</Text>
            </Stack>
            <Accordion defaultIndex={[0, 1, 2]} allowMultiple allowToggle>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Text textStyle={"title"} fontSize="xl">
                        General Information
                      </Text>
                    </Box>

                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={12}>
                  {/* Field Meta */}
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
                    <Stack spacing={1}>
                      <Text textStyle={"label"}>Field Name</Text>
                      <HStack spacing={4} justify="space-between">
                        <Text>{column.display}</Text>
                      </HStack>
                    </Stack>
                    <Stack spacing={1}>
                      <Text textStyle={"label"}>Field Help Text</Text>
                      <HStack spacing={4} justify="space-between">
                        {column.help_text ? (
                          <Text>{column.help_text}</Text>
                        ) : (
                          <Text color="text.softer">n/a</Text>
                        )}
                        {column.help_text && (
                          <IconButton
                            size="sm"
                            onClick={handleOnCopyHelpText}
                            icon={
                              <MaterialIcon
                                fontSize="sm"
                                name="content_copy"
                                variant="outline"
                              />
                            }
                            aria-label="close menu"
                          ></IconButton>
                        )}
                      </HStack>
                    </Stack>
                  </SimpleGrid>
                </AccordionPanel>
              </AccordionItem>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Text textStyle={"title"} fontSize="xl">
                        Field Properties
                      </Text>
                    </Box>

                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={12}>
                  {/* Field Meta */}
                  <Wrap spacing={12}>
                    <Stack spacing={1}>
                      <Text textStyle={"label"}>Field Key</Text>
                      <HStack spacing={4}>
                        <IconTextSmudge
                          iconColor="text.softer"
                          iconName="key"
                          text={column.column_key}
                        ></IconTextSmudge>
                        <IconButton
                          size="sm"
                          onClick={handleOnCopyId}
                          icon={
                            <MaterialIcon
                              fontSize="sm"
                              name="content_copy"
                              variant="outline"
                            />
                          }
                          aria-label="close menu"
                        ></IconButton>
                      </HStack>
                    </Stack>
                    <Stack spacing={1}>
                      <Text textStyle={"label"}>Field Type</Text>
                      <HStack spacing={4} justify="space-between">
                        <IconTextSmudge
                          iconColor="text.soft"
                          iconName={iconNameFieldType}
                          text={column.type}
                        ></IconTextSmudge>
                      </HStack>
                    </Stack>
                    <Stack spacing={1}>
                      <Text textStyle={"label"}>Required</Text>
                      <HStack spacing={4} justify="space-between">
                        {column.is_required && (
                          <IconTextSmudge
                            iconColor="success"
                            iconName="done"
                            text="Field is required"
                          ></IconTextSmudge>
                        )}
                        {!column.is_required && (
                          <IconTextSmudge
                            iconColor="danger"
                            iconName="close"
                            text="Field is not required"
                          ></IconTextSmudge>
                        )}
                      </HStack>
                    </Stack>

                    <Stack spacing={1}>
                      <Text textStyle={"label"}>
                        Protected Health Information
                      </Text>
                      <HStack spacing={4} justify="space-between">
                        {column.is_phi && (
                          <IconTextSmudge
                            iconColor="info"
                            iconName="badge"
                            text="Field is considered PHI"
                          ></IconTextSmudge>
                        )}
                        {!column.is_phi && (
                          <IconTextSmudge
                            iconColor="gray"
                            iconName="badge"
                            text="Field is not considered PHI"
                          ></IconTextSmudge>
                        )}
                      </HStack>
                    </Stack>

                    {column.version && (
                      <Stack spacing={1}>
                        <Text textStyle={"label"}>Field Version</Text>
                        <HStack spacing={4} justify="space-between">
                          <IconTextSmudge
                            iconColor="text.soft"
                            iconName="history"
                            text={`v ${column.version}`}
                          ></IconTextSmudge>
                        </HStack>
                      </Stack>
                    )}
                    {column.is_passthrough && (
                      <Stack spacing={1}>
                        <Text textStyle={"label"}>Passthrough</Text>
                        <HStack spacing={4} justify="space-between">
                          <IconTextSmudge
                            iconColor="text.soft"
                            iconName="water"
                            text="Not defined in a schema"
                          ></IconTextSmudge>
                        </HStack>
                      </Stack>
                    )}
                    {column.is_mapped && (
                      <Stack spacing={1}>
                        <Text textStyle={"label"}>Mapped</Text>
                        <HStack spacing={4} justify="space-between">
                          <IconTextSmudge
                            iconColor="text.soft"
                            iconName="transform"
                            text="Mapped from original value"
                          ></IconTextSmudge>
                        </HStack>
                      </Stack>
                    )}

                    {column.fieldversion_id && (
                      <Stack spacing={1}>
                        <Text textStyle={"label"}>Field Version Id</Text>
                        <HStack spacing={4}>
                          <IconTextSmudge
                            iconColor="text.softer"
                            iconName="fingerprint"
                            text={column.fieldversion_id}
                          ></IconTextSmudge>
                          <IconButton
                            size="sm"
                            onClick={handleOnCopyFieldId}
                            icon={
                              <MaterialIcon
                                fontSize="sm"
                                name="content_copy"
                                variant="outline"
                              />
                            }
                            aria-label="close menu"
                          ></IconButton>
                        </HStack>
                      </Stack>
                    )}
                  </Wrap>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Text textStyle={"title"} fontSize="xl">
                        Schema Information
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={12}>
                  <Wrap spacing={12}>
                    <Stack spacing={1}>
                      <Text textStyle={"label"}>Schema</Text>
                      <HStack spacing={4} justify="space-between">
                        <Text>{column.schema_title}</Text>
                      </HStack>
                    </Stack>

                    {column.mappings && column.mappings.length && (
                      <Stack flex={1}>
                        <FieldMappings
                          mappings={column.mappings}
                        ></FieldMappings>
                      </Stack>
                    )}
                  </Wrap>
                </AccordionPanel>
              </AccordionItem>

              {OPTION_COLUMNS_TYPES.includes(column.type) && (
                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text textStyle={"title"} fontSize="xl">
                          Field Type Options
                        </Text>
                      </Box>

                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={12}>
                    <Wrap spacing={12}>
                      <Stack spacing={1}>
                        <Text textStyle={"label"}>Field Type</Text>
                        <HStack spacing={4} justify="space-between">
                          <IconTextSmudge
                            iconColor="text.soft"
                            iconName={iconNameFieldType}
                            text={column.type}
                          ></IconTextSmudge>
                        </HStack>
                      </Stack>
                      <Stack flex={1}>
                        <FieldTypeOptions
                          options={column.options}
                        ></FieldTypeOptions>
                      </Stack>
                    </Wrap>
                  </AccordionPanel>
                </AccordionItem>
              )}
            </Accordion>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button onClick={onClose}>Close</Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
